import { Maximize2, Pause, Play, Volume1, Volume2, VolumeX } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { cn } from '@/lib/cn'
import { formatDuration } from '@/lib/format'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import styles from './VideoPlayer.module.scss'

export interface VideoPlayerProps {
  /** URL to the MP4. Backend must answer with Accept-Ranges for seek. */
  src: string | null
  poster?: string | null
  /** Overlay rendered across the top of the frame (design: big display title). */
  titleOverlay?: ReactNode
  /** Optional small caption overlaid below the title (design: karaoke caption). */
  captionOverlay?: ReactNode
  className?: string
}

const PLAYBACK_RATES = [1, 1.5, 2] as const

export function VideoPlayer({
  src,
  poster,
  titleOverlay,
  captionOverlay,
  className,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [rate, setRate] = useState<number>(1)
  const [speedOpen, setSpeedOpen] = useState(false)
  const [volume, setVolume] = useLocalStorage<number>('prism:videoPlayer:volume', 1)
  const [muted, setMuted] = useLocalStorage<boolean>('prism:videoPlayer:muted', false)
  // Default to 9:16 (Shorts) until the <video> reports its real
  // dimensions on ``loadedmetadata``. Long-form POV videos are 16:9
  // and were getting squashed into the vertical container otherwise.
  const [aspectRatio, setAspectRatio] = useState<string>('9 / 16')

  const togglePlay = useCallback(() => {
    const el = videoRef.current
    if (!el) return
    if (el.paused) void el.play()
    else el.pause()
  }, [])

  const changeRate = useCallback((next: number) => {
    const el = videoRef.current
    if (!el) return
    el.playbackRate = next
    setRate(next)
    setSpeedOpen(false)
  }, [])

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m
      // If unmuting from a 0 volume, restore to a sensible default so the
      // user actually hears something instead of just toggling state.
      if (!next && volume === 0) {
        setVolume(1)
      }
      return next
    })
  }, [volume])

  const onVolumeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const next = Number(e.target.value)
    setVolume(next)
    setMuted(next === 0)
  }, [])

  // Sync volume/muted state to the underlying <video> element.
  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.volume = volume
    el.muted = muted
  }, [volume, muted])

  const toggleFullscreen = useCallback(() => {
    const node = containerRef.current
    if (!node) return
    if (document.fullscreenElement === node) {
      void document.exitFullscreen()
    } else {
      void node.requestFullscreen()
    }
  }, [])

  // Close the speed popover on outside click.
  useEffect(() => {
    if (!speedOpen) return
    const handler = () => {
      setSpeedOpen(false)
    }
    window.addEventListener('click', handler)
    return () => {
      window.removeEventListener('click', handler)
    }
  }, [speedOpen])

  // Reset time when src changes.
  useEffect(() => {
    setCurrentTime(0)
    setDuration(0)
    setPlaying(false)
  }, [src])

  // Listen for an external "show me the thumbnail frame" request — fired by
  // ThumbDock so the user can preview the current thumb in the actual player
  // without leaving the page. Pause + seek to 0 (which is the prepended
  // thumbnail frame in the composed MP4).
  useEffect(() => {
    const handler = () => {
      const el = videoRef.current
      if (!el) return
      el.pause()
      el.currentTime = 0
    }
    window.addEventListener('prism:reset-player', handler)
    return () => {
      window.removeEventListener('prism:reset-player', handler)
    }
  }, [])

  const onProgressClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const el = videoRef.current
      if (!el || !duration) return
      const rect = e.currentTarget.getBoundingClientRect()
      const ratio = (e.clientX - rect.left) / rect.width
      el.currentTime = Math.min(Math.max(0, ratio * duration), duration)
    },
    [duration],
  )

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div
      ref={containerRef}
      className={cn(styles.wrap, className)}
      style={{ aspectRatio }}
    >
      {src ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={videoRef}
          src={src}
          poster={poster ?? undefined}
          className={styles.video}
          onTimeUpdate={(e) => {
            setCurrentTime(e.currentTarget.currentTime)
          }}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget
            setDuration(v.duration)
            // Browsers report videoWidth/videoHeight after metadata loads.
            // Falling back to 9/16 keeps the container stable when the
            // video element doesn't expose dimensions (encrypted streams,
            // network errors).
            if (v.videoWidth > 0 && v.videoHeight > 0) {
              setAspectRatio(`${v.videoWidth.toString()} / ${v.videoHeight.toString()}`)
            }
          }}
          onPlay={() => {
            setPlaying(true)
          }}
          onPause={() => {
            setPlaying(false)
          }}
          onClick={togglePlay}
          playsInline
        />
      ) : (
        <div className={styles.placeholder}>Sem vídeo ainda</div>
      )}

      {titleOverlay ? <div className={styles.titleOverlay}>{titleOverlay}</div> : null}
      {captionOverlay ? <div className={styles.captionOverlay}>{captionOverlay}</div> : null}

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.playBtn}
          onClick={togglePlay}
          aria-label={playing ? 'Pausar' : 'Reproduzir'}
        >
          {playing ? <Pause /> : <Play />}
        </button>
        <span className={styles.time}>{formatDuration(currentTime)}</span>
        <button
          type="button"
          className={styles.progress}
          onClick={onProgressClick}
          aria-label="Linha do tempo"
        >
          <span className={styles.progressFill} style={{ width: `${pct.toString()}%` }} />
        </button>
        <span className={styles.time}>{formatDuration(duration)}</span>
        <span className={styles.speedWrap}>
          <button
            type="button"
            className={styles.speedBtn}
            onClick={(e) => {
              // Stop propagation so the document-level "click outside"
              // listener doesn't immediately close the popover we just opened.
              e.stopPropagation()
              setSpeedOpen((s) => !s)
            }}
            aria-label={`Velocidade atual ${rate.toString()}x`}
            aria-haspopup="menu"
            aria-expanded={speedOpen}
          >
            {rate}x
          </button>
          {speedOpen ? (
            <div className={styles.speedPop} role="menu">
              {PLAYBACK_RATES.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="menuitem"
                  className={cn(styles.speedOpt, r === rate && styles.speedOptActive)}
                  onClick={(e) => {
                    e.stopPropagation()
                    changeRate(r)
                  }}
                >
                  {r}x
                </button>
              ))}
            </div>
          ) : null}
        </span>
        <span className={styles.volumeWrap}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={toggleMute}
            aria-label={muted || volume === 0 ? 'Ativar som' : 'Silenciar'}
          >
            {muted || volume === 0 ? (
              <VolumeX />
            ) : volume < 0.5 ? (
              <Volume1 />
            ) : (
              <Volume2 />
            )}
          </button>
          <div className={styles.volumePop}>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={muted ? 0 : volume}
              onChange={onVolumeChange}
              className={styles.volumeSlider}
              aria-label="Volume"
            />
          </div>
        </span>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={toggleFullscreen}
          aria-label="Tela cheia"
        >
          <Maximize2 />
        </button>
      </div>
    </div>
  )
}
